<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useUserStore } from '@/stores/user';
    import { useEventsStore } from '@/stores/events';
    import { useSitesStore } from '@/stores/sites';

    const userStore = useUserStore();

    // Variables
    const mapView = ref(false);
    // const { events: userEvents } = userStore.events;
    const { events: userEvents } = storeToRefs(userStore);

    // Events Store
    const loading = ref(true);
    const eventsStore = useEventsStore();
    const eventsData = eventsStore.eventsData?.records;

    // Sites Store to get Event Geocoordinates
    const sitesStore = useSitesStore();
    const sites = sitesStore.sitesData;

    // Computed
    const events = computed(() => {
        if (userEvents.value.length) {
            const rawEvents = eventsData.filter((el) => {
                return userEvents.value.includes(el.id);
            });
            return formatEvents(rawEvents);
        }
        return [];
    });

    // Methods
    // Contains failures from the map subtree so they cannot take the page down —
    // see the matching note in page.vue.
    onErrorCaptured((error) => {
        console.error('[my-ohc events] contained an error from the favourites panel:', error);
        return false;
    });

    function setView(view) {
        mapView.value = view;
    }

    function formatEvents(events) {
        let items = events;
        items.forEach((el) => {
            const site = sites.find((site) => site.id === el.fields.Site[0]);
            el.fields.Geocoordinates = site.fields.Geocoordinates;
        });
        return items;
    }
</script>

<template>
    <div class="my-ohc-events">
        <BlocksMyOhcNav @viewToggle="setView" />
        <!--
            CLIENT ONLY for the same reason as the sites panel in page.vue:
            favourited events come from a client-side call, so SSR rendered the
            empty state and the client swapped it out once the data landed. That
            swap unmounts the map below, and vue3-google-map's clusterer throws
            from a beforeUnmount hook, which corrupts Vue's teardown and kills the
            whole page rather than just the map.

            The sites panel is where this was actually reported (Asana O2-68 /
            O2-37); this panel is the same shape — empty-state alert plus a
            BlocksMap sibling, both driven by client-only user data — so it is
            fixed alongside rather than waiting for someone to hit it. It has
            already caused a blank panel here once before.

            Note /my-ohc/itineraries is deliberately NOT changed: it renders no
            map, so a swap there has nothing to throw and is harmless.
        -->
        <ClientOnly>
        <section class="favorites-and-intineraries-wrapper">
            <!-- Resolved events, not the raw id list — see page.vue. -->
            <BlocksTurquoiseAlert v-if="!events.length" :type="'events'" :overlay="mapView" />
            <div class="my-favorites-wrapper">
                <section class="events">
                    <BlocksMap v-if="mapView" :locations="events" :mapOnly="!userEvents?.length" content="event" navId="my-ohc-nav" :offset="36" />
                    <BlocksLiveEvents v-else :showHeader="false" :showViewAll="false" :items="events"></BlocksLiveEvents>
                </section>
            </div>
        </section>
        </ClientOnly>
    </div>
</template>

<style lang="scss" scoped>
    .my-ohc-events {
        max-width: $hd;
        margin: auto;
        min-height: 400px;
        padding-top: 16px;
        
        &.page {
            @media (min-width: $tablet) {
                max-width: $hd;
                margin: auto;
            }
        }
        .favorites-and-intineraries-wrapper {
            z-index: 0;
            position: relative;
        }
    }
</style>
